import { DatePicker as AntDatePicker, theme } from 'antd';
import styled, { css } from 'styled-components';
import type { GlobalToken } from 'antd';
import type { Dayjs } from 'dayjs';
import {
  ComponentSize,
  getControlHeight,
  getBorderRadius,
} from '../../utils/tokenHelpers';

export type DatePickerStatus = 'default' | 'warning' | 'error' | 'success';

export interface DatePickerProps {
  /** Input status for validation feedback */
  status?: DatePickerStatus;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether to allow clearing the value */
  allowClear?: boolean;
  /** Format of the date display */
  format?: string;
  /** Default value */
  defaultValue?: Dayjs;
  /** Controlled value */
  value?: Dayjs | null;
  /** Callback when date changes */
  onChange?: (date: Dayjs | null, dateString: string | string[]) => void;
  /** Additional class name */
  className?: string;
  /** Test id */
  'data-testid'?: string;
  /** id for the input */
  id?: string;
  /** DatePicker size following Figma design system */
  size?: ComponentSize;
}

const getStatusStyles = (token: GlobalToken, status: DatePickerStatus) => {
  const statusMap = {
    default: css`
      &.ant-picker {
        background: ${token.colorBgContainer};
        border-color: ${token.colorBorderSecondary};

        &:hover:not(.ant-picker-disabled) {
          border-color: ${token.colorPrimaryHover};
        }

        &.ant-picker-focused {
          border-color: ${token.colorPrimary};
          box-shadow: 0 0 0 2px ${token.colorPrimaryBg};
        }
      }
    `,
    warning: css`
      &.ant-picker {
        background: ${token.colorWarningBg};
        border-color: ${token.colorWarning};

        &:hover:not(.ant-picker-disabled) {
          border-color: ${token.colorWarningHover};
        }

        &.ant-picker-focused {
          border-color: ${token.colorWarning};
          box-shadow: 0 0 0 2px ${token.colorWarningBg};
        }
      }
    `,
    error: css`
      &.ant-picker {
        background: ${token.colorErrorBg};
        border-color: ${token.colorError};

        &:hover:not(.ant-picker-disabled) {
          border-color: ${token.colorErrorHover};
        }

        &.ant-picker-focused {
          border-color: ${token.colorError};
          box-shadow: 0 0 0 2px ${token.colorErrorBg};
        }
      }
    `,
    success: css`
      &.ant-picker {
        background: ${token.colorSuccessBg};
        border-color: ${token.colorSuccess};

        &:hover:not(.ant-picker-disabled) {
          border-color: ${token.colorSuccessHover};
        }

        &.ant-picker-focused {
          border-color: ${token.colorSuccess};
          box-shadow: 0 0 0 2px ${token.colorSuccessBg};
        }
      }
    `,
  };
  return statusMap[status];
};

interface StyledDatePickerProps {
  $token: GlobalToken;
  $status: DatePickerStatus;
  $height: number;
  $borderRadius: number;
}

const StyledDatePicker = styled(AntDatePicker)<StyledDatePickerProps>`
  &.ant-picker {
    width: 100%;
    height: ${({ $height }) => $height}px;
    padding: ${({ $token }) => `${$token.paddingXS}px ${$token.paddingSM}px`};
    border-radius: ${({ $borderRadius }) => $borderRadius}px;
    font-family: ${({ $token }) => $token.fontFamily};
    font-size: ${({ $token }) => $token.fontSizeSM}px;
    transition: all ${({ $token }) => $token.motionDurationMid} ease;
  }

  ${({ $token, $status }) => getStatusStyles($token, $status)}

  &.ant-picker-disabled {
    background: ${({ $token }) => $token.colorBgLayout};
    border-color: ${({ $token }) => $token.colorBorderSecondary};
    color: ${({ $token }) => $token.colorTextDisabled};
    cursor: not-allowed;
  }

  .ant-picker-input > input {
    font-family: ${({ $token }) => $token.fontFamily};
    font-size: ${({ $token }) => $token.fontSizeSM}px;
    color: ${({ $token }) => $token.colorText};

    &::placeholder {
      color: ${({ $token }) => $token.colorTextDisabled};
    }
  }

  .ant-picker-suffix {
    color: ${({ $token }) => $token.colorTextTertiary};
  }

  .ant-picker-clear {
    color: ${({ $token }) => $token.colorTextDisabled};

    &:hover {
      color: ${({ $token }) => $token.colorTextSecondary};
    }
  }
`;

/**
 * DatePicker component based on Libertify Design System
 *
 * Now uses Ant Design theme tokens for consistent styling and size support.
 *
 * @example
 * ```tsx
 * import { DatePicker } from '@libertify/ui';
 * import dayjs from 'dayjs';
 *
 * // Basic usage
 * <DatePicker
 *   placeholder="Select date"
 *   onChange={(date, dateString) => console.log(dateString)}
 * />
 *
 * // Different sizes
 * <DatePicker size="s" placeholder="Small picker" />
 * <DatePicker size="l" placeholder="Large picker" />
 *
 * // With default value
 * <DatePicker defaultValue={dayjs('2024-01-15')} />
 *
 * // Custom format
 * <DatePicker format="MM/DD/YYYY" />
 * ```
 */
export const DatePicker = ({
  status = 'default',
  placeholder = 'Select date',
  disabled = false,
  allowClear = true,
  format = 'YYYY-MM-DD',
  defaultValue,
  value,
  onChange,
  className,
  'data-testid': testId,
  id,
  size = 'm',
}: DatePickerProps) => {
  const { token } = theme.useToken();
  const height = getControlHeight(token, size);
  const borderRadius = getBorderRadius(token, size);

  const handleChange = onChange
    ? (date: unknown, dateString: string | string[]) => {
        onChange(date as Dayjs | null, dateString);
      }
    : undefined;

  return (
    <StyledDatePicker
      $token={token}
      $status={status}
      $height={height}
      $borderRadius={borderRadius}
      placeholder={placeholder}
      disabled={disabled}
      allowClear={allowClear}
      format={format}
      defaultValue={defaultValue}
      value={value}
      onChange={handleChange}
      className={className}
      data-testid={testId}
      id={id}
    />
  );
};

export default DatePicker;
